import { HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Inline "?" help icon next to form labels. Tap/hover to see a one-line explanation.
 * Optional `learnMoreId` deep-links to a Help Center article on click.
 *
 * Usage:
 *   <Label>Rate type <FieldHelp text="Weekday day = 6am-8pm…" learnMoreId="timesheets" /></Label>
 */
export default function FieldHelp({ text, learnMoreId }) {
  const content = (
    <div className="max-w-[260px] text-xs leading-relaxed">
      <p>{text}</p>
      {learnMoreId && (
        <a
          href={`/help#${learnMoreId}`}
          className="text-client font-bold underline mt-1.5 inline-block"
          onClick={(e) => e.stopPropagation()}
          data-testid={`field-help-link-${learnMoreId}`}
        >
          Learn more →
        </a>
      )}
    </div>
  );

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            tabIndex={-1}
            data-testid="field-help-trigger"
            className="ml-1.5 inline-flex items-center justify-center text-muted-foreground hover:text-client transition-colors"
            onClick={(e) => e.preventDefault()}
            aria-label="Help"
          >
            <HelpCircle className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className="rounded-xl border-2 px-3 py-2 shadow-lg z-50">
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
